<template>
  <div>
    <Teleport to="body">
      <div v-if="forceMobile || isMobile">
        <!-- Navbar -->
        <div class="tw-fixed tw-top-0 tw-left-0 tw-w-full tw-z-[50] dark:tw-bg-white tw-bg-black">
          <div
            class="tw-flex tw-justify-between tw-items-center tw-px-6 tw-py-5 desktop:tw-px-[80px] dark:tw-bg-black tw-bg-white"
          >
            <!-- Logo -->
            <img
              :src="$cdn(isDarkMode ? 'images/home/logo_dark.svg' : 'images/home/logo_light.svg')"
              alt="Logo"
              class="tw-w-[82px] tw-h-[29px]"
            >
            <!-- Hamburger Menu or Close Icon -->
            <button class="tw-p-2 tw-rounded-md " @click="toggleMenu">
              <img
                v-if="!menuOpen"
                :src="$cdn(isDarkMode ? 'images/home/hamburger_dark.png' : 'images/home/hamburger.png')"
                alt="Hamburger Menu"
                class="tw-w-8 tw-h-8"
              >
              <img
                v-else
                :src="$cdn(isDarkMode ? 'images/home/close_sidebar_dark.png' : 'images/home/close_sidebar_light.png')"
                alt="Close Sidebar"
                class="tw-w-8 tw-h-8"
              >
            </button>
          </div>
        </div>

        <!-- Full Screen Overlay Menu -->
        <div
          v-show="menuOpen"
          id="mobileOverlay"
          class="tw-fixed tw-inset-0 tw-bg-black/70 tw-z-40 animate__animated animate__fadeInDown"
          :class="{
          }"
          @click="toggleMenu"
        >
          <!-- Sidebar Content -->
          <div
            class="tw-flex tw-flex-col tw-w-full tw-h-full tw-p-4 desktop:tw-px-[80px] desktop:tw-pt-16 desktop:tw-pb-8 tw-overflow-auto dark:tw-bg-black tw-bg-white"
            @click.stop
          >
            <div class="tw-flex tw-justify-between tw-items-center tw-pb-[120px]" />

            <!-- Navigation Links -->
            <div class="tw-flex tw-flex-col ">
              <a
                v-for="(section, index) in sections"
                :key="index"
                :href="`#${section}`"
                class="dark:tw-bg-black tw-bg-white"
                :class="[
                  'tw-text-h4-bold-desktop tw-uppercase tw-flex tw-items-center tw-gap-[4px]',
                  activeSection === section
                    ? 'tw-text-ap-persian-red' // Active red color
                    : 'dark:tw-text-white tw-text-ap-nero', // Inactive state text color
                  getPaddingClasses(section), // Add padding based on section
                ]"
                @click="updateActiveSection(section)"
              >
                <!-- Active section indicator (red bar) -->
                <span
                  v-show="activeSection === section"
                  class="active-section-indicator animate__animated animate__zoomIn"
                  :class="{
                    'tw-transform-translate-x-[-100%]': activeSection !== section,
                  }"
                />
                <span
                  :class="{
                    ' tw-duration-[800ms]': true, // Apply transition to all properties
                    'tw-translate-x-[4%]': activeSection === section, // Animate shift when first span is shown
                  }"
                >
                  {{ sectionLabel(section) }}
                </span>
              </a>
            </div>

            <!-- Dark Mode Toggle -->
            <div class="tw-my-[100px]">
              <img
                :src="$cdn(isDarkMode ? 'images/home/toggle-dark.svg' : 'images/home/toggle-light.svg')"
                alt="Toggle"
                class="tw-w-[80px] tw-h-[40px] tw-flex-shrink-0"
                :class="{
                  'tw-cursor-not-allowed tw-opacity-50': activeSection === 'our-work',
                  'hover:tw-opacity-80 hover:tw-scale-105 tw-opacity-100': activeSection !== 'our-work',
                  'tw-transition-all tw-duration-[500ms] tw-ease-in-out': true,
                }"
                @click="darkModeToggle"
              >
            </div>

            <!-- Rights Reserved and Policies -->
            <div
              class="tw-mt-auto tw-text-center tw-text-sm tw-mx-[24px] dark:tw-text-ap-dim-grey tw-text-ap-nobel tw-font-inter"
            >
              <p class="tw-flex tw-justify-center tw-text-label-md-mobile tw-w-[263px] tw-mx-auto">
                © 2025 The Next Experience Pte Ltd. All Rights Reserved
              </p>
              <div
                class="tw-flex tw-justify-center tw-text-label-md-mobile tw-gap-1 tw-pt-2  dark:tw-text-ap-very-light-grey tw-text-ap-nero"
              >
                <button
                  class="tw-text-inherit dark:tw-text-ap-very-light-grey tw-text-ap-nero"
                  @click="openPrivacyPolicyModal"
                >Privacy Policy</button>
                <span class="tw-text-gray-600">|</span>
                <button
                  class="tw-text-inherit dark:tw-text-ap-very-light-grey tw-text-ap-nero"
                  @click="openCookiePolicyModal"
                >Cookie Policy</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
    <!-- Privacy Policy Modal -->
    <TeleportModal v-model:visible="modalPrivacyPolicyOpened">
      <PrivacyPolicy @show-cookie-policy="openCookiePolicyModal" />
    </TeleportModal>

    <!-- Cookie Policy Modal -->
    <TeleportModal v-model:visible="modalCookiePolicyOpened">
      <CookiePolicy />
    </TeleportModal>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { watchDarkMode } from '../../support/helper';
import CookiePolicy from '../cookie-policy/CookiePolicy.vue';
import PrivacyPolicy from '../privacy-policy/PrivacyPolicy.vue';
import TeleportModal from '../shared/TeleportModal.vue';

// Dark Mode State
const darkMode = ref(false); // true for dark mode, false for light mode
const isMobile = ref(false); // Reactive state to check if it's mobile
const forceMobile = ref(false); // Force mobile mode state passed from parent
const menuOpen = ref(false); // Sidebar open/closed state
const isDarkMode = ref(false);

let stopObserving: () => void;

const props = defineProps({
  isForceMobile: Boolean,
});

const activeSection = ref(''); // Tracks the currently active section
const sections = ['about-us', 'what-we-do', 'our-work', 'get-in-touch'];

// Toggle Sidebar Menu
const toggleMenu = () => {
  if (menuOpen.value) {
    const menuElement = document.getElementById('mobileOverlay');

    if (menuElement) {
      // Add fadeOutUp animation before closing
      menuElement.classList.remove('animate__fadeInDown');
      menuElement.classList.add('animate__fadeOutUp');
    }
    setTimeout(() => {
      menuOpen.value = false;
    }, 300); // Match the duration of fadeOutUp animation
  } else {
    const menuElement = document.getElementById('mobileOverlay');

    if (menuElement) {
      // Add fadeInDown class when opening
      menuElement.classList.remove('animate__fadeOutUp');
      menuElement.classList.add('animate__fadeInDown');
    }
    menuOpen.value = true;
  }
};
const updateActiveSection = (section: string) => {
  activeSection.value = section;
  const menuElement = document.getElementById('mobileOverlay');

  if (menuElement && menuOpen.value) {
    // Remove fadeInDown class (if present) and add fadeOutUp class
    menuElement.classList.remove('animate__fadeInDown');
    menuElement.classList.add('animate__fadeOutUp');

    setTimeout(() => {
      menuOpen.value = false; // Close the menu after the fadeOutUp animation
    }, 2000); // Duration of the fadeOutUp animation (adjusted to 2000ms)
  }
};

function getPaddingClasses(section: string): string {
  switch (section) {
    case 'about-us':
      return 'tw-pt-4 tw-pb-8'; // Padding top: 16px, Padding bottom: 32px
    case 'what-we-do':
    case 'our-work':
      return 'tw-pb-8'; // Padding bottom: 32px
    case 'get-in-touch':
      return 'tw-pb-4'; // Padding bottom: 16px
    default:
      return ''; // Default padding if none specified
  }
}

const sectionLabel = (section: string) => {
  switch (section) {
    case 'about-us':
      return 'About Us';
    case 'what-we-do':
      return 'What We Do';
    case 'our-work':
      return 'Our Work';
    case 'get-in-touch':
      return 'Get in Touch';
    default:
      return '';
  }
};

// Dark Mode Toggle
const darkModeToggle = () => {
  darkMode.value = !darkMode.value;
};

watch(menuOpen, (newVal) => {
  if (newVal) {
    // When the menu is opened, ensure fadeInDown animation is applied
    const menuElement = document.getElementById('mobileOverlay');
    if (menuElement) {
      menuElement.classList.remove('animate__fadeOutUp');
      menuElement.classList.add('animate__fadeInDown');
    }
  } else {
    // When closing the menu, fadeOutUp animation should be triggered
    const menuElement = document.getElementById('mobileOverlay');
    if (menuElement) {
      menuElement.classList.remove('animate__fadeInDown');
      menuElement.classList.add('animate__fadeOutUp');
    }
  }
});

watch(darkMode, (newVal) => {
  if (newVal) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
});

watch(menuOpen, (isOpen) => {
  if (isOpen) {
    document.body.classList.add('no-scroll');
  } else {
    document.body.classList.remove('no-scroll');
  }
});

const observeSections = () => {
  const updateOnScroll = () => {
    sections.forEach((sectionId) => {
      const element = document.getElementById(sectionId);
      if (element) {
        const rect = element.getBoundingClientRect();
        const isInView = rect.top >= 0 && rect.top <= window.innerHeight / 2;
        if (isInView) {
          updateActiveSection(sectionId); // Update active section
          // TOGGLE DARK MODE ONLY AT 1 COMPONENT
          // YOU CANT TOGGLE AT SIDEBAR MOBILE AND ABOUT US AND SIDEBAR COMPONENT AT THE SAME TIME
          if (sectionId === 'our-work' && !darkMode.value) {
            darkModeToggle();
          }
        }
      }
    });
  };
  window.addEventListener('scroll', updateOnScroll);
  updateOnScroll(); // Initial check on load
};

onMounted(() => {
  isMobile.value = window.innerWidth <= 1280;
  forceMobile.value = props.isForceMobile;
  observeSections();
  stopObserving = watchDarkMode((value: any) => {
    isDarkMode.value = value;
  });
});

onUnmounted(() => {
  window.removeEventListener('scroll', observeSections);
});

//Privacy policy modal
const modalPrivacyPolicyOpened = ref(false);

function openPrivacyPolicyModal() {
  modalCookiePolicyOpened.value = false;
  modalPrivacyPolicyOpened.value = true;
  document.body.style.overflow = 'hidden';
}

//Cookie policy modal
const modalCookiePolicyOpened = ref(false);

function openCookiePolicyModal() {
  modalPrivacyPolicyOpened.value = false;
  modalCookiePolicyOpened.value = true;
  document.body.style.overflow = 'hidden';
}
</script>

<style scoped>
/* Active section indicator animation */
.active-section-indicator {
  background-color: #dc1e1e;
  width: 32px;
  height: 2px;
  opacity: 0;
  /* Initially hidden */
}

.animate__animated.animate__zoomIn {
  --animate-duration: 1000ms;
}
.animate__animated.animate__fadeInDown{
  --animate-duration: 800ms;
}


.animate__animated.animate__fadeOutUp{
  --animate-duration: 800ms;
}

a .active-section-indicator {
  opacity: 1;
}

</style>